<h2 mat-dialog-title>{{ reelId ? 'Edit SituReel' : 'New SituReel' }}</h2>
<form [formGroup]="form" (ngSubmit)="save()">
  <mat-dialog-content class="reel-edit-content">

    <div class="reel-meta" fxLayout="row wrap" fxLayoutGap="16px grid">
      <mat-form-field appearance="outline" fxFlex="40">
        <mat-label>Name</mat-label>
        <input matInput formControlName="name" maxlength="120" placeholder="e.g. Tilos Range — Theme Showcase">
        <mat-error *ngIf="form.get('name')?.hasError('required')">Name is required.</mat-error>
      </mat-form-field>

      <mat-form-field appearance="outline" fxFlex="55">
        <mat-label>Description</mat-label>
        <input matInput formControlName="description" maxlength="280" placeholder="What a prospect sees alongside the reel">
      </mat-form-field>
    </div>

    <div *ngIf="shareUrl()" class="share-url">
      <mat-icon class="share-icon">share</mat-icon>
      <span class="share-label">Share URL</span>
      <span class="share-value">{{ shareUrl() }}</span>
    </div>

    <div class="items-header" fxLayout="row" fxLayoutAlign="space-between center">
      <div>
        <div class="items-title">Items <span class="items-count">({{ items.length }})</span></div>
        <div class="items-hint">Pick which item loads first by tapping its star. Drag the up/down buttons to reorder.</div>
      </div>
      <div class="add-item-buttons">
        <button mat-flat-button color="primary" type="button" (click)="openPicker()">
          <mat-icon>add</mat-icon>
          Add render
        </button>
        <button mat-stroked-button color="primary" type="button" (click)="openProductPicker()">
          <mat-icon>kitchen</mat-icon>
          Add product
        </button>
      </div>
    </div>

    <div class="items-list" formArrayName="items" *ngIf="items.length > 0; else emptyHint">
      <ng-container *ngFor="let ctrl of items.controls; let i = index; trackBy: trackByIndex">
        <div class="item-row"
             [class.is-default]="isDefault(i)"
             [class.is-expanded]="expandedItemIndex === i"
             [formGroupName]="i">

          <button mat-icon-button type="button"
                  class="default-toggle"
                  [matTooltip]="isDefault(i) ? 'Default home (loads first)' : 'Set as default home'"
                  (click)="setDefault(i)">
            <mat-icon [color]="isDefault(i) ? 'primary' : ''">{{ isDefault(i) ? 'star' : 'star_border' }}</mat-icon>
          </button>

          <div class="item-thumb">
            <img *ngIf="!isThumbFailed(ctrl)"
                 [src]="getThumbUrl(ctrl)"
                 alt="thumbnail"
                 loading="lazy"
                 (error)="onThumbError(ctrl)">
            <div *ngIf="isThumbFailed(ctrl)" class="item-thumb-fallback">
              <mat-icon>{{ getFallbackIcon(ctrl) }}</mat-icon>
            </div>
            <div *ngIf="hasOverride(i)" class="item-thumb-override-badge" matTooltip="Custom thumbnail">
              <mat-icon>image</mat-icon>
            </div>
          </div>

          <div class="item-fields" fxLayout="column" fxFlex>
            <div fxLayout="row" fxLayoutGap="12px" fxLayoutAlign="start center">
              <mat-form-field appearance="outline" class="dense" fxFlex>
                <mat-label>Label</mat-label>
                <input matInput formControlName="label" maxlength="60">
              </mat-form-field>
              <mat-form-field appearance="outline" class="dense" fxFlex>
                <mat-label>Subtitle</mat-label>
                <input matInput formControlName="subtitle" maxlength="80">
              </mat-form-field>
            </div>
            <div class="item-meta">
              <span class="product-chip">{{ productLabel(ctrl.value.productName) }}</span>
              <span class="artifact-id" [matTooltip]="ctrl.value.artifactId">{{ ctrl.value.artifactId }}</span>
              <button *ngIf="supportsStartView(ctrl)"
                      type="button"
                      class="start-view-chip"
                      [class.has-value]="hasStartView(ctrl)"
                      [matTooltip]="describeStartView(ctrl)"
                      (click)="openStartViewPicker(i)">
                <mat-icon>my_location</mat-icon>
                <span>{{ hasStartView(ctrl) ? 'Edit starting view' : 'Set starting view' }}</span>
              </button>
            </div>
          </div>

          <div class="item-actions" fxLayout="column" fxLayoutAlign="center center">
            <button mat-icon-button type="button" matTooltip="Move up" [disabled]="i === 0" (click)="moveUp(i)">
              <mat-icon>keyboard_arrow_up</mat-icon>
            </button>
            <button mat-icon-button type="button" matTooltip="Move down" [disabled]="i === items.length - 1" (click)="moveDown(i)">
              <mat-icon>keyboard_arrow_down</mat-icon>
            </button>
          </div>

          <button mat-icon-button type="button"
                  [matTooltip]="hasOverride(i) ? 'Edit custom thumbnail' : 'Override thumbnail'"
                  [color]="hasOverride(i) ? 'primary' : ''"
                  (click)="toggleExpand(i)">
            <mat-icon>{{ expandedItemIndex === i ? 'expand_less' : 'image_search' }}</mat-icon>
          </button>

          <button mat-icon-button type="button" matTooltip="Remove" color="warn" (click)="removeItem(i)">
            <mat-icon>delete_outline</mat-icon>
          </button>
        </div>

        <div *ngIf="expandedItemIndex === i" class="item-override-panel" [formGroupName]="i">
          <div class="override-header">
            <mat-icon>image</mat-icon>
            <span>Custom thumbnail</span>
            <small>Paste a public URL to replace the auto-generated thumbnail for this item.</small>
          </div>
          <div fxLayout="row" fxLayoutGap="8px" fxLayoutAlign="start center">
            <mat-form-field appearance="outline" class="dense" fxFlex>
              <mat-label>Thumbnail URL</mat-label>
              <input matInput formControlName="thumbnailOverrideUrl" placeholder="https://...">
            </mat-form-field>
            <button mat-stroked-button type="button" (click)="clearOverride(i)" [disabled]="!hasOverride(i)">
              Use default
            </button>
          </div>
        </div>
      </ng-container>
    </div>

    <ng-template #emptyHint>
      <div class="empty-hint">
        <mat-icon>video_library</mat-icon>
        <div>This reel has no items yet.</div>
        <div class="small">Click <strong>Add render</strong> for panoramas, facades, plans or renders — or <strong>Add product</strong> to feature a catalog item.</div>
      </div>
    </ng-template>

  </mat-dialog-content>

  <mat-dialog-actions align="end">
    <button mat-button type="button" (click)="cancel()" [disabled]="saving">Cancel</button>
    <button mat-flat-button color="primary" type="submit" [disabled]="saving">
      <mat-icon *ngIf="!saving">save</mat-icon>
      <mat-spinner *ngIf="saving" diameter="18"></mat-spinner>
      Save
    </button>
  </mat-dialog-actions>
</form>
